import { boundMethod } from 'autobind-decorator';
import { AsyncStatus, Satisfaction } from 'lib-shared-legacy-types';
import queryString from 'query-string';
import { PureComponent } from 'react';

import { SupportedCSATLanguage } from 'app/views/layout/views/settings/views/satisfaction/utils/supportedCSATLanguages/supportedCSATLanguages';
import { RouteComponentProps } from 'hocs/router';

import { Csat } from '../../common/types';
import { EmailPage } from '../EmailPage';
import { EmailCsatSettingTabs } from '../EmailPage/EmailPage';
import { FormValues } from '../EmailPage/components/EmailCSATSettingsForm/EmailCSATSettingsForm';
import { EmailCSAT } from '../EmailPage/types';

import { formatEmailCustomisationFormValuesForApi } from './utilities';

export interface EmailAccountSetting {
  address: string;
  enabled: boolean;
  settings: Satisfaction.EmailCsatSettings | null;
}

// We need some CSAT specific languages on top of the supported ones.
export interface LanguageSetting extends SupportedCSATLanguage {
  settings: Satisfaction.EmailCsatSettings | null;
}

export interface DispatchProps {
  loadAllEmails: () => void;
  loadEmailSettingsAllLevels: () => void;
  initializeEmailCsat: () => void;
  createGlobalSettings: (
    settings: Satisfaction.CreateEmailCsatSettingsParams,
  ) => void;
  updateGlobalSettings: (
    settings: Satisfaction.UpdateEmailCsatSettingsParams,
  ) => void;
  updateEmailLevelSettings: (
    settings: EmailCSAT.EmailAccountStatusChange,
  ) => void;
  updateLanguageLevelSettings: (
    settings: Partial<Satisfaction.EmailCsatSettings>,
  ) => void;
}

export interface StateProps {
  globalSettings: {
    values: Satisfaction.EmailCsatSettings | null;
    asyncStatus: AsyncStatus;
  };

  emailAccountsSettings: {
    list: EmailAccountSetting[];
    asyncStatus: AsyncStatus;
  };

  languagesSettings: {
    list: LanguageSetting[];
    asyncStatus: AsyncStatus;
  };
}

type InitiallyActiveTabQueryParamName = 'initiallyActiveTab';
export const initiallyActiveTabQueryParamName: InitiallyActiveTabQueryParamName =
  'initiallyActiveTab' as const;

type QueryT = {
  [key in InitiallyActiveTabQueryParamName]:
    | string
    | string[]
    | null
    | undefined;
};

type Props = DispatchProps & StateProps & RouteComponentProps;

export class EmailPanel extends PureComponent<Props> {
  componentDidMount() {
    this.props.loadEmailSettingsAllLevels();
    this.props.loadAllEmails();
  }

  private getEnrichedLanguagesWithSettings(): EmailCSAT.Language[] {
    return this.props.languagesSettings.list.map(
      ({ code, label, settings }): EmailCSAT.Language => ({
        isSetup: settings !== null,
        ratingStyle: settings?.customization.look_and_feel ?? undefined,
        enabled: settings?.enabled || false,
        name: label,
        code,
        settings,
      }),
    );
  }

  @boundMethod
  private redirectToLanguageCustomizationPage(language: EmailCSAT.Language) {
    const pathname = language.isSetup
      ? `/settings/satisfaction/email/${language.code}/edit/${language.settings?.id}`
      : `/settings/satisfaction/email/${language.code}/create/`;

    this.props.navigate({ pathname });
  }

  getGlobalSettingsInitialValues(): FormValues {
    const {
      delay = '',
      sender_email = null,
      reply_to_email = null,
    } = this.props.globalSettings.values?.customization || {};

    return {
      delay: delay?.toString(),
      senderEmail: sender_email,
      replyToEmail: reply_to_email,
    };
  }

  @boundMethod
  handleSaveGlobalSettings(formValues: FormValues): void {
    if (this.isEmailCsatSetup()) {
      this.editGlobalSettings(formValues);
    }
  }

  private editGlobalSettings(formValues: FormValues) {
    if (!this.props.globalSettings.values) return;
    const customization = formatEmailCustomisationFormValuesForApi(formValues);
    if (customization) {
      this.props.updateGlobalSettings({
        ...this.props.globalSettings.values,
        customization,
      });
    }
  }

  @boundMethod
  private handleGlobalStatusChange(change: Csat.CsatStatusChange) {
    // If we have global settings already, update it
    if (this.props.globalSettings.values) {
      this.props.updateGlobalSettings({
        ...this.props.globalSettings.values,
        enabled: change.newStatus,
      });
    } else {
      // Otherwise csat is not initialized, so initialize if needed.
      if (change.newStatus === true) {
        this.props.initializeEmailCsat();
      }
    }
  }

  @boundMethod
  private handleLanguageStatusChange({
    language,
    newStatus,
  }: EmailCSAT.LanguageCustomizationStatusChange): void {
    // the default language should not be allowed to be disabled.
    if (EmailCSAT.defaultLanguage === language.code) {
      return;
    }

    const payload: Partial<Satisfaction.EmailCsatSettings> = {
      id: language.settings?.id,
      enabled: newStatus,
      channel: 'email',
    };

    this.props.updateLanguageLevelSettings(payload);
  }

  private isEmailCsatActive() {
    return this.props.globalSettings.values?.enabled || false;
  }

  private isEmailCsatSetup() {
    return Boolean(this.props.globalSettings.values);
  }

  private isLoadingGlobalSettings() {
    return this.props.globalSettings.asyncStatus === 'loading';
  }

  private isLoadingEmailLevelSettings() {
    return this.props.emailAccountsSettings.asyncStatus === 'loading';
  }

  private getInitiallyActiveTab(): EmailCsatSettingTabs {
    let { initiallyActiveTab } = queryString.parse(
      this.props.location.search,
    ) as QueryT;

    if (Array.isArray(initiallyActiveTab)) {
      initiallyActiveTab = initiallyActiveTab[0];
    }

    const cleanedUpValue = initiallyActiveTab?.trim().toLowerCase();

    if (!cleanedUpValue) {
      return EmailCsatSettingTabs.Accounts;
    }

    switch (cleanedUpValue) {
      case EmailCsatSettingTabs.Accounts.toLowerCase():
        return EmailCsatSettingTabs.Accounts;

      case EmailCsatSettingTabs.Customizations.toLowerCase():
        return EmailCsatSettingTabs.Customizations;

      default:
        return EmailCsatSettingTabs.Accounts;
    }
  }

  render() {
    return (
      <EmailPage
        onGlobalStatusChange={this.handleGlobalStatusChange}
        isEmailCsatActive={this.isEmailCsatActive()}
        isEmailCsatSetup={this.isEmailCsatSetup()}
        globalSettingsInitialValue={this.getGlobalSettingsInitialValues()}
        onSettingsSave={this.handleSaveGlobalSettings}
        existingEmails={this.props.emailAccountsSettings.list}
        onAccountStatusChange={this.props.updateEmailLevelSettings}
        isLoadingEmailsList={this.isLoadingEmailLevelSettings()}
        languages={this.getEnrichedLanguagesWithSettings()}
        onLanguageStatusChange={this.handleLanguageStatusChange}
        onLanguageEdit={this.redirectToLanguageCustomizationPage}
        isLoadingGlobalSettings={this.isLoadingGlobalSettings()}
        defaultLanguage={EmailCSAT.defaultLanguage}
        initiallyActiveTab={this.getInitiallyActiveTab()}
      />
    );
  }
}
